// components/AccountStatusModal.jsx
import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';

const AccountStatusModal = ({ 
  type, 
  message, 
  trialInfo, 
  onClose, 
  onSubscribe, 
  onContactSupport 
}) => {
  
  // ✅ Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  // Get icon based on type
  const getIcon = () => {
    switch (type) {
      case 'expired':
      case 'trial_expired':
        return '⏰';
      case 'inactive':
        return '🚫';
      case 'suspended':
        return '⛔';
      default:
        return '⚠️';
    }
  };

  // Get title based on type
  const getTitle = () => {
    switch (type) {
      case 'expired':
        return 'Account Expireddd';
      case 'trial_expired':
        return 'Trial Period Ended';
      case 'inactive':
        return 'Account Deactivated';
      case 'suspended':
        return 'Account Suspended';
      default:
        return 'Account Issue';
    }
  };

  // Get border color based on type
  const getBorderColor = () => {
    switch (type) {
      case 'expired':
      case 'trial_expired':
        return '#f97316';
      case 'inactive':
        return '#6b7280';
      case 'suspended':
        return '#ef4444';
      default:
        return '#f97316';
    }
  };

  // Check if we should show subscribe button
  const showSubscribeButton = type === 'expired' || type === 'trial_expired';

  // ✅ Modal content
  const modalContent = (
    <div style={modalStyles.overlay}>
      <div 
        style={{
          ...modalStyles.card,
          borderColor: getBorderColor()
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close X Button */}
        <button 
          style={modalStyles.closeX}
          onClick={onClose}
          onMouseEnter={(e) => {
            e.target.style.background = 'rgba(255, 255, 255, 0.15)';
            e.target.style.color = '#fff';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = 'rgba(255, 255, 255, 0.05)';
            e.target.style.color = '#94a3b8';
          }}
        >
          ✕
        </button>

        {/* Icon */}
        <div style={modalStyles.icon}>
          {getIcon()}
        </div>

        {/* Title */}
        <h2 style={modalStyles.title}>{getTitle()}</h2>

        {/* Message */}
        <p style={modalStyles.message}>{message}</p>

        {/* Trial Info Box */}
        {trialInfo && trialInfo.daysUntilReset && (
          <div style={modalStyles.trialInfoBox}>
            <p style={modalStyles.trialInfoText}>
              ⏳ Your trial will reset in <strong>{trialInfo.daysUntilReset} days</strong>
            </p>
          </div>
        )}

        {/* Features List */}
        {showSubscribeButton && (
          <div style={modalStyles.featuresList}>
            <div style={modalStyles.featureItem}>
              <span style={modalStyles.featureCheck}>✓</span>
              <span>Unlimited party management</span>
            </div>
            <div style={modalStyles.featureItem}>
              <span style={modalStyles.featureCheck}>✓</span>
              <span>Complete billing & invoicing</span>
            </div>
            <div style={modalStyles.featureItem}>
              <span style={modalStyles.featureCheck}>✓</span>
              <span>Medicine inventory tracking</span>
            </div>
            <div style={{...modalStyles.featureItem, borderBottom: 'none'}}>
              <span style={modalStyles.featureCheck}>✓</span>
              <span>Expiry date alerts & reports</span>
            </div>
          </div>
        )}

        {/* Subscribe Button */}
        {showSubscribeButton && (
          <button 
            style={modalStyles.subscribeBtn}
            onClick={onSubscribe}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 6px 20px 0 rgba(5, 150, 105, 0.5)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 14px 0 rgba(5, 150, 105, 0.4)';
            }}
          >
            🚀 Subscribe Now
          </button>
        )}

        {/* Divider */}
        <div style={modalStyles.divider}>
          <div style={modalStyles.dividerLine}></div>
          <span style={modalStyles.dividerText}>or</span>
          <div style={modalStyles.dividerLine}></div>
        </div>

        {/* Contact Support Button */}
        <button 
          style={modalStyles.supportBtn}
          onClick={onContactSupport}
          onMouseEnter={(e) => {
            e.target.style.background = 'rgba(255, 255, 255, 0.1)';
            e.target.style.color = '#fff';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = 'rgba(255, 255, 255, 0.05)';
            e.target.style.color = '#94a3b8';
          }}
        >
          ✉️ Contact Support
        </button>

        {/* Back to Login Button */}
        <button 
          style={modalStyles.backBtn}
          onClick={onClose}
          onMouseEnter={(e) => {
            e.target.style.background = 'rgba(100, 116, 139, 0.3)';
            e.target.style.color = '#e2e8f0';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = 'rgba(100, 116, 139, 0.2)';
            e.target.style.color = '#94a3b8';
          }}
        >
          ← Back to Login
        </button>

        {/* Reset Info */}
        {trialInfo && trialInfo.daysUntilReset && (
          <p style={modalStyles.resetInfo}>
            💡 Your free trial will automatically reset in {trialInfo.daysUntilReset} days.
          </p>
        )}
      </div>
    </div>
  );

  // ✅ Use React Portal to render modal at document body level
  return ReactDOM.createPortal(
    modalContent,
    document.body
  );
};

// ✅ All styles as a JavaScript object
const modalStyles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(10, 15, 25, 0.98)',
    zIndex: 999999,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    backdropFilter: 'blur(15px)',
    WebkitBackdropFilter: 'blur(15px)',
  },
  card: {
    maxWidth: '440px',
    width: '100%',
    backgroundColor: 'rgba(30, 41, 59, 0.98)',
    borderRadius: '24px',
    padding: '40px',
    textAlign: 'center',
    boxShadow: '0 25px 60px -12px rgba(0, 0, 0, 0.7)',
    position: 'relative',
    border: '2px solid',
  },
  closeX: {
    position: 'absolute',
    top: '15px',
    right: '15px',
    width: '40px',
    height: '40px',
    background: 'rgba(255, 255, 255, 0.05)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '50%',
    color: '#94a3b8',
    fontSize: '18px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease',
  },
  icon: {
    fontSize: '70px',
    marginBottom: '20px',
  },
  title: {
    color: '#fff',
    fontSize: '26px',
    fontWeight: '700',
    margin: '0 0 12px 0',
    fontFamily: "'Poppins', sans-serif",
  },
  message: {
    color: '#94a3b8',
    fontSize: '15px',
    lineHeight: '1.7',
    margin: '0 0 25px 0',
    fontFamily: "'Poppins', sans-serif",
  },
  trialInfoBox: {
    background: 'rgba(129, 230, 217, 0.1)',
    border: '1px solid rgba(129, 230, 217, 0.2)',
    borderRadius: '12px',
    padding: '15px',
    marginBottom: '25px',
  },
  trialInfoText: {
    color: '#81e6d9',
    fontSize: '14px',
    margin: 0,
    fontFamily: "'Poppins', sans-serif",
  },
  featuresList: {
    textAlign: 'left',
    background: 'rgba(0, 0, 0, 0.25)',
    borderRadius: '12px',
    padding: '15px 20px',
    marginBottom: '25px',
    border: '1px solid rgba(255, 255, 255, 0.05)',
  },
  featureItem: {
    color: '#cbd5e1',
    fontSize: '13px',
    padding: '10px 0',
    borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    fontFamily: "'Poppins', sans-serif",
  },
  featureCheck: {
    color: '#10b981',
    fontSize: '16px',
  },
  subscribeBtn: {
    width: '100%',
    padding: '16px',
    background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
    color: '#fff',
    border: 'none',
    borderRadius: '12px',
    fontSize: '16px',
    fontWeight: '700',
    cursor: 'pointer',
    marginBottom: '12px',
    boxShadow: '0 4px 14px 0 rgba(5, 150, 105, 0.4)',
    transition: 'transform 0.2s, box-shadow 0.2s',
    fontFamily: "'Poppins', sans-serif",
  },
  divider: {
    display: 'flex',
    alignItems: 'center',
    margin: '20px 0',
    gap: '15px',
  },
  dividerLine: {
    flex: 1,
    height: '1px',
    background: 'rgba(255, 255, 255, 0.1)',
  },
  dividerText: {
    color: '#64748b',
    fontSize: '12px',
    fontFamily: "'Poppins', sans-serif",
  },
  supportBtn: {
    width: '100%',
    padding: '14px',
    background: 'rgba(255, 255, 255, 0.05)',
    color: '#94a3b8',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '12px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background 0.2s, color 0.2s',
    marginBottom: '12px',
    fontFamily: "'Poppins', sans-serif",
  },
  backBtn: {
    width: '100%',
    padding: '14px',
    background: 'rgba(100, 116, 139, 0.2)',
    color: '#94a3b8',
    border: '1px solid rgba(100, 116, 139, 0.3)',
    borderRadius: '12px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s',
    fontFamily: "'Poppins', sans-serif",
  },
  resetInfo: {
    color: '#64748b',
    fontSize: '12px',
    marginTop: '18px',
    fontFamily: "'Poppins', sans-serif",
    lineHeight: '1.5',
  },
};

export default AccountStatusModal;